import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { adminApiService } from '../../services/adminApi';
import CreditPackModal from './CreditPackModal';

const CourtTypeManagement = () => {
  const { adminUser } = useAdmin();
  const [courtTypes, setCourtTypes] = useState([]);
  const [packModalType, setPackModalType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCourtType, setEditingCourtType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    thaiName: '',
    index: 0,
    active: true,
    rules: {
      thai: [''],
      english: ['']
    }
  });

  useEffect(() => {
    fetchCourtTypes();
  }, []);

  const fetchCourtTypes = async () => {
    try {
      setLoading(true);
      const data = await adminApiService.getCourtTypes(adminUser.idToken);
      setCourtTypes(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCourtType) {
        await adminApiService.updateCourtType(adminUser.idToken, editingCourtType.id, formData);
      } else {
        await adminApiService.createCourtType(adminUser.idToken, formData);
      }
      setShowModal(false);
      setEditingCourtType(null);
      resetForm();
      fetchCourtTypes();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (courtType) => {
    setEditingCourtType(courtType);
    setFormData({
      name: courtType.name,
      thaiName: courtType.thaiName,
      index: courtType.index,
      active: courtType.active,
      rules: {
        thai: courtType.rules?.thai || [''],
        english: courtType.rules?.english || ['']
      }
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('คุณแน่ใจหรือไม่ที่จะลบประเภทสนามนี้?')) {
      return;
    }
    try {
      await adminApiService.deleteCourtType(adminUser.idToken, id);
      fetchCourtTypes();
    } catch (err) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      thaiName: '',
      index: 0,
      active: true,
      rules: {
        thai: [''],
        english: ['']
      }
    });
  };

  const openCreateModal = () => {
    setEditingCourtType(null);
    resetForm();
    setShowModal(true);
  };

  // Helper functions for managing rules
  const addRule = (language) => {
    setFormData({
      ...formData,
      rules: {
        ...formData.rules,
        [language]: [...formData.rules[language], '']
      }
    });
  };

  const removeRule = (language, index) => {
    const newRules = formData.rules[language].filter((_, i) => i !== index);
    setFormData({
      ...formData,
      rules: {
        ...formData.rules,
        [language]: newRules.length > 0 ? newRules : ['']
      }
    });
  };

  const updateRule = (language, index, value) => {
    const newRules = [...formData.rules[language]];
    newRules[index] = value;
    setFormData({
      ...formData,
      rules: {
        ...formData.rules,
        [language]: newRules
      }
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">ประเภทสนาม</h2>
        <button
          onClick={openCreateModal}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
        >
          เพิ่มประเภทสนาม
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {courtTypes.map((courtType) => (
            <li key={courtType.id} className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="text-2xl">🏀</span>
                  </div>
                  <div className="ml-4">
                    <div className="flex items-center">
                      <p className="text-sm font-medium text-gray-900">{courtType.name}</p>
                      <span className="ml-2 text-sm text-gray-500">({courtType.thaiName})</span>
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        ลำดับ: {courtType.index}
                      </span>
                    </div>
                    <div className="flex items-center mt-1">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          courtType.active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {courtType.active ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setPackModalType(courtType)}
                    className="text-amber-600 hover:text-amber-800 text-sm font-medium"
                  >
                    💳 แพ็กเกจ
                  </button>
                  <button
                    onClick={() => handleEdit(courtType)}
                    className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                  >
                    แก้ไข
                  </button>
                  <button
                    onClick={() => handleDelete(courtType.id)}
                    className="text-red-600 hover:text-red-900 text-sm font-medium"
                  >
                    ลบ
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingCourtType ? 'แก้ไขประเภทสนาม' : 'เพิ่มประเภทสนาม'}
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">ชื่อ (ภาษาอังกฤษ)</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">ชื่อ (ภาษาไทย)</label>
                      <input
                        type="text"
                        value={formData.thaiName}
                        onChange={(e) => setFormData({ ...formData, thaiName: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">ลำดับการแสดงผล</label>
                      <input
                        type="number"
                        value={formData.index}
                        onChange={(e) => setFormData({ ...formData, index: parseInt(e.target.value) })}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="active"
                        checked={formData.active}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
                        เปิดใช้งาน
                      </label>
                    </div>
                  </div>

                  {/* Rules Section */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-900">กฎของสนาม</h4>
                    
                    {/* Thai Rules */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">กฎ (ภาษาไทย)</label>
                      <div className="space-y-2">
                        {formData.rules.thai.map((rule, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={rule}
                              onChange={(e) => updateRule('thai', index, e.target.value)}
                              className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              placeholder="ใส่กฎข้อนี้..."
                            />
                            {formData.rules.thai.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeRule('thai', index)}
                                className="text-red-600 hover:text-red-800"
                              >
                                ลบ
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => addRule('thai')}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          + เพิ่มกฎ
                        </button>
                      </div>
                    </div>

                    {/* English Rules */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">กฎ (ภาษาอังกฤษ)</label>
                      <div className="space-y-2">
                        {formData.rules.english.map((rule, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={rule}
                              onChange={(e) => updateRule('english', index, e.target.value)}
                              className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Enter rule..."
                            />
                            {formData.rules.english.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeRule('english', index)}
                                className="text-red-600 hover:text-red-800"
                              >
                                ลบ
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => addRule('english')}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          + เพิ่มกฎ
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingCourtType(null);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    ยกเลิก
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    {editingCourtType ? 'อัปเดต' : 'สร้าง'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {packModalType && (
        <CreditPackModal
          courtType={packModalType}
          idToken={adminUser.idToken}
          onClose={() => setPackModalType(null)}
        />
      )}
    </div>
  );
};

export default CourtTypeManagement; 